import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, resolveMedia } from "../lib/api";

const PlayerContext = createContext(null);

export const formatTime = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export const AudioProvider = ({ children }) => {
  const audioRef = useRef(typeof Audio !== "undefined" ? new Audio() : null);
  const [track, setTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.9);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.preload = "metadata";
    audio.volume = 0.9;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onMeta = () => setDuration(audio.duration);
    const onEnded = () => setPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  const playTrack = useCallback((t) => {
    const audio = audioRef.current;
    if (!audio || !t?.audio_url) return;
    if (track && track.src === resolveMedia(t.audio_url)) {
      if (audio.paused) {
        audio.play().then(() => setPlaying(true)).catch(() => {});
      } else {
        audio.pause();
        setPlaying(false);
      }
      return;
    }
    const src = resolveMedia(t.audio_url);
    audio.src = src;
    audio.currentTime = 0;
    audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    setTrack({
      src,
      title: t.title,
      tag: t.tag || t.category || "The Royal Vault",
      cover: t.artwork_url ? resolveMedia(t.artwork_url) : "",
      slug: t.slug || "",
    });
    if (t.slug) api.post(`/vault/${t.slug}/play`).catch(() => {});
  }, [track]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !track) return;
    if (audio.paused) {
      audio.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      audio.pause();
      setPlaying(false);
    }
  }, [track]);

  const seek = useCallback((t) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = t;
    setCurrentTime(t);
  }, []);

  const setVolume = useCallback((v) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = v;
    setVolumeState(v);
  }, []);

  const close = useCallback(() => {
    const audio = audioRef.current;
    if (audio) audio.pause();
    setPlaying(false);
    setTrack(null);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  return (
    <PlayerContext.Provider
      value={{ track, playing, currentTime, duration, volume, playTrack, toggle, seek, setVolume, close }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => useContext(PlayerContext);
